import { Dispatch, SetStateAction, memo, useMemo, useState } from 'react';
import { Grid } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { ScheduledDeliveriesOrderData } from '.';
import moment from 'moment';
import { MRT_PaginationState } from 'mantine-react-table';
import OrderStatuses from '../../../enum/OrderStatuses';

export const buildScheduledDeliveriesColumns = (t: ReturnType<typeof useTranslation>['t']) => [
  {
    id: 'storeName',
    accessorFn: (row: ScheduledDeliveriesOrderData) => row.storeName || '-',
    header: t('FieldName:store') || 'Sucursal'
  },
  {
    id: 'folio',
    accessorFn: (row: ScheduledDeliveriesOrderData) => row.folio || '-',
    header: t('FieldName:order') || 'Orden'
  },
  {
    id: 'customerName',
    accessorFn: (row: ScheduledDeliveriesOrderData) => row.customerName || '-',
    header: t('FieldName:customer') || 'Cliente'
  },
  {
    id: 'customerPhone',
    accessorFn: (row: ScheduledDeliveriesOrderData) => row.customerPhone || '-',
    header: t('FieldName:phone') || 'Teléfono'
  },
  {
    id: 'deliveryDate',
    accessorFn: (row: ScheduledDeliveriesOrderData) =>
      row.deliveryDate ? moment(row.deliveryDate).format('DD/MM/YYYY') : '-',
    header: t('FieldName:deliveryDate') || 'Fecha Entrega'
  },
  {
    id: 'servicesText',
    accessorFn: (row: ScheduledDeliveriesOrderData) => row.servicesText || '-',
    header: t('FieldName:services') || 'Servicios'
  },
  {
    id: 'total',
    accessorFn: (row: ScheduledDeliveriesOrderData) => formatMoneyString((row.total || 0).toFixed(2)),
    header: t('FieldName:total') || 'Total'
  },
  {
    id: 'paidAmount',
    accessorFn: (row: ScheduledDeliveriesOrderData) => formatMoneyString((row.paidAmount || 0).toFixed(2)),
    header: t('FieldName:paid') || 'Pagado'
  },
  {
    id: 'balance',
    accessorFn: (row: ScheduledDeliveriesOrderData) => formatMoneyString((row.balance || 0).toFixed(2)),
    header: t('FieldName:amountDue') || 'Saldo'
  },
  {
    id: 'status',
    accessorFn: (row: ScheduledDeliveriesOrderData) =>
      t(
        `FieldName:orderStatuses.${
          OrderStatuses[row.status.toUpperCase() as keyof typeof OrderStatuses]
        }`
      ),
    header: t('FieldName:status') || 'Estatus'
  }
];

export default memo(function TableView({
  tableInfoData = [],
  totalRowsCount = 0,
  isError = false,
  isLoading = false,
  columnVisibility,
  setColumnVisibility
}: {
  tableInfoData: ScheduledDeliveriesOrderData[];
  totalRowsCount: number;
  isError: boolean;
  isLoading: boolean;
  columnVisibility?: Record<string, boolean>;
  setColumnVisibility?: Dispatch<SetStateAction<Record<string, boolean>>>;
}) {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const { t } = useTranslation(['FieldName']);
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<Record<string, boolean>>(
    {}
  );
  const columnVisibilityState = columnVisibility ?? internalColumnVisibility;
  const setColumnVisibilityState = setColumnVisibility ?? setInternalColumnVisibility;

  const columns = useMemo(() => buildScheduledDeliveriesColumns(t), [t]);

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            tableData={tableInfoData}
            isError={isError}
            paginationIsLoading={isLoading}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              enablePagination: true, // Enable pagination for client-side pagination
              manualPagination: false, // Client-side pagination since all data is loaded
              enableColumnActions: false,
              enableHiding: true,
              state: { columnVisibility: columnVisibilityState },
              onColumnVisibilityChange: setColumnVisibilityState
            }}
            pagination={pagination}
            setPagination={setPagination}
            numPages={Math.ceil(totalRowsCount / pagination.pageSize)}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
